<script lang="ts">
	import { isSupportedFile } from '#lib/pipeline';
	import { t } from '#lib/i18n/state.svelte';

	interface Props {
		onfile: (file: File) => void;
		disabled?: boolean;
	}

	let { onfile, disabled = false }: Props = $props();

	let dragging = $state(false);
	let error = $state<string | null>(null);
	let inputEl: HTMLInputElement;
	let dragDepth = 0;

	const ACCEPT = '.pdf,.docx,.tex,.latex,.ltx,.txt,.md,.markdown';

	function pick() {
		if (disabled) return;
		error = null;
		inputEl?.click();
	}

	function handleFiles(files: FileList | null) {
		if (!files || files.length === 0) return;
		const file = files[0];
		if (!isSupportedFile(file)) {
			error = t('drop.errorUnsupported');
			return;
		}
		if (file.size > 40 * 1024 * 1024) {
			error = t('drop.errorSize');
			return;
		}
		if (file.size === 0) {
			error = t('drop.errorEmpty');
			return;
		}
		error = null;
		onfile(file);
	}

	function onChange(e: Event) {
		const input = e.currentTarget as HTMLInputElement;
		handleFiles(input.files);
		input.value = '';
	}

	function onDragEnter(e: DragEvent) {
		e.preventDefault();
		if (disabled) return;
		dragDepth++;
		dragging = true;
	}

	function onDragOver(e: DragEvent) {
		e.preventDefault();
		if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
	}

	function onDragLeave(e: DragEvent) {
		e.preventDefault();
		dragDepth = Math.max(0, dragDepth - 1);
		if (dragDepth === 0) dragging = false;
	}

	function onDrop(e: DragEvent) {
		e.preventDefault();
		dragDepth = 0;
		dragging = false;
		if (disabled) return;
		handleFiles(e.dataTransfer?.files ?? null);
	}

	function onKeydown(e: KeyboardEvent) {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			pick();
		}
	}
</script>

<div
	class="dropzone"
	class:is-dragging={dragging}
	class:is-disabled={disabled}
	role="button"
	tabindex={disabled ? -1 : 0}
	aria-label={t('drop.aria')}
	aria-disabled={disabled}
	onclick={pick}
	onkeydown={onKeydown}
	ondragenter={onDragEnter}
	ondragover={onDragOver}
	ondragleave={onDragLeave}
	ondrop={onDrop}
>
	<input
		bind:this={inputEl}
		type="file"
		accept={ACCEPT}
		class="sr-only"
		onchange={onChange}
		tabindex="-1"
		aria-hidden="true"
	/>

	<div class="drop-art" aria-hidden="true">
		<svg viewBox="0 0 64 64" width="56" height="56">
			<path
				d="M16 10h22l12 12v32a4 4 0 0 1-4 4H16a4 4 0 0 1-4-4V14a4 4 0 0 1 4-4z"
				fill="none"
				stroke="currentColor"
				stroke-width="2.4"
				stroke-linejoin="round"
			/>
			<path d="M38 10v12h12" fill="none" stroke="currentColor" stroke-width="2.4" />
			<path
				d="M32 46V28m0 0-7 7m7-7 7 7"
				fill="none"
				stroke="currentColor"
				stroke-width="2.6"
				stroke-linecap="round"
				stroke-linejoin="round"
			/>
		</svg>
	</div>

	<div class="drop-copy">
		<p class="headline">{t('drop.headline')}</p>
		<p class="sub">{t('drop.subBefore')} {t('drop.subAfter')}</p>
	</div>

	<div class="formats" aria-hidden="true">
		<span class="fmt">PDF</span>
		<span class="fmt">DOCX</span>
		<span class="fmt">TeX</span>
		<span class="fmt">TXT</span>
		<span class="sep"></span>
		<span class="limit">{t('drop.limit')}</span>
	</div>

	{#if error}
		<p class="error" role="alert">{error}</p>
	{/if}
</div>

<style>
	.dropzone {
		position: relative;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 0.9rem;
		padding: clamp(2rem, 5vw, 3.25rem) 1.5rem;
		border: 1.5px dashed var(--line-strong);
		border-radius: 20px;
		background:
			linear-gradient(180deg, rgb(255 255 255 / 0.75), rgb(255 255 255 / 0.35)),
			var(--paper-2);
		cursor: pointer;
		transition:
			border-color 0.2s var(--ease),
			background 0.2s var(--ease),
			transform 0.2s var(--ease),
			box-shadow 0.2s var(--ease);
		text-align: center;
		user-select: none;
	}
	.dropzone:hover:not(.is-disabled) {
		border-color: var(--accent);
		background:
			linear-gradient(180deg, rgb(255 255 255 / 0.9), rgb(227 241 238 / 0.5)),
			var(--paper-2);
	}
	.dropzone:focus-visible {
		outline: 2px solid var(--accent);
		outline-offset: 3px;
	}
	.is-dragging {
		border-color: var(--accent);
		border-style: solid;
		background: var(--accent-soft);
		transform: scale(1.008);
		box-shadow: var(--shadow);
	}
	.is-disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
	.drop-art {
		color: var(--accent);
		background: #fff;
		border: 1px solid var(--line);
		border-radius: 16px;
		padding: 0.7rem;
		box-shadow: var(--shadow-sm);
		display: grid;
		place-items: center;
	}
	.headline {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		margin: 0 auto;
		padding: 0.55rem 1.35rem;
		border-radius: 8px;
		background: var(--accent-ink);
		color: #fff;
		font-family: var(--font-ui);
		font-size: 0.88rem;
		font-weight: 600;
		cursor: pointer;
	}
	.sub {
		margin: 0;
		color: var(--ink-2);
		font-size: 0.92rem;
	}
	.formats {
		display: flex;
		align-items: center;
		gap: 0.4rem;
		flex-wrap: wrap;
		justify-content: center;
	}
	.fmt {
		font-size: 0.68rem;
		font-weight: 700;
		letter-spacing: 0.06em;
		color: var(--muted);
		background: #fff;
		border: 1px solid var(--line);
		border-radius: 6px;
		padding: 0.13rem 0.42rem;
	}
	.sep {
		width: 1px;
		height: 14px;
		background: var(--line-strong);
		margin-inline: 0.2rem;
	}
	.limit {
		font-size: 0.74rem;
		color: var(--muted);
	}
	.error {
		margin: 0;
		color: var(--critical);
		background: var(--critical-soft);
		border: 1px solid #f2c9c4;
		border-radius: 9px;
		padding: 0.55rem 0.85rem;
		font-size: 0.85rem;
		max-width: 34rem;
	}

	@media (max-width: 480px) {
		.dropzone {
			padding: 1.6rem 1rem;
			gap: 0.75rem;
			border-radius: 16px;
		}
		.drop-art svg {
			width: 44px;
			height: 44px;
		}
		.headline {
			font-size: 1.25rem;
		}
		.sub {
			font-size: 0.86rem;
		}
	}
</style>
